/* Built In Imports */
/* External Imports */
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Flex, Heading } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { useRouter } from 'next/router';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import config from '@config';
import { getUtmParamsFromUrl } from '@components/Utility/utils';

/* Configs */
import { IeoInpersonSt } from '@ieo/Common/CompLangConfig';
import NextLink from 'next/link';
import { mixPanel } from '@utils/Mixpanel';
import SingleImage from './SingleImage';
import { usePathname } from 'next/navigation';
/* Styles */
/* Services */

/**
 *
 * @param {Object} sectionContent
 * @param {Object} prgData
 * @param {String} lang
 * @param {region} region
 * @returns {ReactElement} IeoInPerson
 */
const IeoInPerson = ({ sectionContent, lang, region, prgData, pageData }) => {
  function findField(fieldId) {
    const data = sectionContent?.find(el => el.fieldId === fieldId);
    return data;
  }
  function findModelField(fieldId) {
    const data = sectionContent?.find(el => el._modelApiKey === fieldId);
    return data;
  }
  const indLang = config.indianLang.indexOf('ta') > -1;
  const pathname = usePathname();

  const changeUi =
    findField('ie-lead-gen-img') && findModelField('section_title');
  const isWavesOfBlissWAPage =
    pathname.endsWith('wave-of-bliss-bengaluru-wa') ||
    pathname.endsWith('wave-of-bliss-uttar-pradesh-wa');
  const isWavesOfBlissUPPage = pathname.includes('wave-of-bliss-uttar-pradesh');

  const mixPanelEventTrigger = () => {
    const Mixpanel = mixPanel(window?.location.pathname);
    Mixpanel.track('ie_wave_lead_registration_cta_click', {
      language: isWavesOfBlissWAPage ? 'Whatsapp bot page' : 'Inline form page',
      language: config?.lngList?.[lang],
      cta: 'bottom CTA',
      element: 'REGISTER NOW',
    });
  };

  const getLinkWithUTMParas = () => {
    const baseURL = findModelField('simple_button')?.linkUrl || '';
    if (!baseURL) return baseURL;

    // Break URL into parts
    const url = new URL(baseURL);

    const hash = url.hash;
    url.hash = '';

    // Append your UTM params
    const utmParams = new URLSearchParams(getUtmParamsFromUrl());
    utmParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    if (!url.searchParams.has('utm_campaign')) {
      url.searchParams.set(
        'utm_campaign',
        isWavesOfBlissUPPage ? 'ie_wave_lucknow' : 'ie_wave_bengaluru'
      );
    }

    // Reattach hash at the end
    url.hash = hash;

    return url.toString();
  };

  return (
    <Box bg="#F1EBDF">
      <Box
        px={{ base: '16px', md: '16px' }}
        pt={{
          base: !changeUi ? '47px' : '70px',
          md: !changeUi ? '80px' : '70px',
          lg: !changeUi ? '100px' : '70px',
        }}
        pb={{
          base: !changeUi ? '63px' : '80px',
          md: !changeUi ? '74px' : '129px',
        }}
        maxW={'1140px'}
        margin={!changeUi ? '0 auto' : '56px auto 0 auto'}
      >
        {findField('ie-lead-gen-img') && findModelField('section_title') && (
          <Flex
            mb={{ base: '23px', md: '36px' }}
            flexWrap="wrap"
            flexDir={{ base: 'column-reverse', md: 'row', lg: 'row' }}
          >
            <>
              <Box
                w={{ base: '100%', md: '45%' }}
                mb={{ base: '0px', md: '40px', lg: '0' }}
                mt={{ base: '24px', md: '0' }}
              >
                <Heading
                  {...IeoInpersonSt[lang].SubHeading}
                  color={{ base: '#E86F34', md: '#2B2827' }}
                  as="h3"
                >
                  {findModelField('section_title')?.titleSubtext}
                </Heading>
                <Heading
                  {...IeoInpersonSt[lang].heading}
                  pt={{ base: '11px', md: '8px' }}
                  as="h2"
                  color="#433F3C"
                  id="ieo-in-programs-text"
                  fontSize={{
                    base: '32px',
                    md: '44px',
                    lg: '64px',
                  }}
                >
                  {findModelField('section_title')?.titleText}
                </Heading>
              </Box>
              <Box
                mt={{ base: '-126px' }}
                w={{ base: 'auto', md: '55%', lg: '55%' }}
              >
                <SingleImage
                  px="0"
                  my="0px"
                  textAlign="start"
                  sectionContent={findField('ie-lead-gen-img')}
                />
              </Box>
            </>
          </Flex>
        )}
        {findField('ie-lead-gen-description') && (
          <Flex
            flexDir={{ base: 'column', md: 'column', lg: 'row' }}
            justifyContent="space-between"
            pb={{ base: '32px', md: '36px' }}
          >
            <Box width={{ base: '100%' }}>
              <Box maxW={820}>
                <StructuredTextParser
                  str={render(findField('ie-lead-gen-description')?.body)}
                  sectionContent={findField('ie-lead-gen-description')}
                  maxW="inherit"
                  fontFamily="FedraSansStd-book"
                  fontSize={{ base: '18px', md: '24px' }}
                  lineHeight={{ base: '27px', md: '36px' }}
                  color="#6D6358"
                  mt={{ base: '0px', md: '0px' }}
                  className="ieo-in-person-text"
                />
              </Box>
            </Box>
          </Flex>
        )}

        {findModelField('simple_button') && (
          <Box pb={{ base: '20px', md: '85px' }}>
            <NextLink
              onClick={mixPanelEventTrigger}
              href={getLinkWithUTMParas()}
              passHref
            >
              <Box display="flex" alignItems="start">
                <Box
                  mt="0px"
                  maxW="max-content"
                  textAlign="center"
                  {...IeoInpersonSt[lang].AttendText}
                  color="#F37021"
                  fontSize={{ base: '22px' }}
                  lineHeight={{ base: '33px' }}
                >
                  {findModelField('simple_button')?.buttonText}
                </Box>

                <Box px="3px">
                  <ChevronRightIcon fontSize="33" color="#F37021" />
                </Box>
              </Box>
            </NextLink>
          </Box>
        )}
        {findField('ie-lead-gen-divider') && (
          <Flex
            flexDir="column"
            w="100%"
            mb={{ base: '44px', md: '0' }}
            display={{ base: 'flex', md: 'none' }}
          >
            <StructuredTextParser
              className="ieoInPerson"
              maxW="100%"
              str={render(findField('ie-lead-gen-divider')?.body)}
              sectionContent={findField('ie-lead-gen-divider')}
            />
          </Flex>
        )}
        <Flex
          flexDir={{ base: 'column', md: 'column', lg: 'row' }}
          alignItems={{ base: 'start', md: 'start', lg: 'center' }}
          justifyContent={{ base: 'start', md: 'start', lg: 'center' }}
        >
          <Box
            width={{ base: '100%', lg: '50%' }}
            mb={{ base: '26px', lg: '0' }}
          >
            <StructuredTextParser
              mt="0"
              textAlign="left"
              pr={{ base: '20px', md: '40px !important' }}
              color="#433F3C"
              str={render(findField('contact-us-title')?.body)}
              sectionContent={findField('contact-us-title')}
              {...IeoInpersonSt[lang].heading}
              maxW={{ base: '100%', lg: '600px', md: '100%', xl: '770px' }}
            />
          </Box>

          <Box w={{ base: '100%', md: '100%', lg: '50%' }}>
            {findField('contact-us') && (
              <Flex mb="10px" justifyContent={'left'}>
                <Box
                  w="30px"
                  h="30px"
                  mr={{ base: '12px', md: '22px' }}
                  mt={{
                    base: '5px',
                    md: (region === 'us' || region === 'ca') && '10px',
                  }}
                >
                  <SingleImage
                    my="0"
                    px="0"
                    sectionContent={findField('contact-us-email')}
                  />
                </Box>
                <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                  <StructuredTextParser
                    mt="0"
                    maxW="400px"
                    fontSize={{ base: '16px', md: '22px' }}
                    str={render(findField('contact-us')?.body)}
                    fontFamily="FedraSansStd-book"
                    className="contactUs"
                  />
                </Box>
              </Flex>
            )}
            {findField('contact-us-number') && (
              <Flex justifyContent={'left'}>
                <Box
                  w="30px"
                  h="30px"
                  mr={{ base: '12px', md: '22px' }}
                  mt={{
                    base: '5px',
                    md: (region === 'us' || region === 'ca') && '10px',
                  }}
                >
                  <SingleImage
                    my="0"
                    px="0"
                    sectionContent={findField('contact-us-phone')}
                  />
                </Box>
                <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                  <StructuredTextParser
                    mt="0"
                    maxW="400px"
                    fontSize={{ base: '16px', md: '22px' }}
                    str={render(findField('contact-us-number')?.body)}
                    fontFamily="FedraSansStd-book"
                    className="contactUs"
                  />
                </Box>
              </Flex>
            )}
          </Box>
        </Flex>
        <style global jsx>
          {`
            .contactUs a {
              color: #f37021;
              font-family: FedraSansStd-medium !important;
            }
            .ieoInPerson {
              max-width: 100% !important;
            }
            .ieo-in-person-text strong {
              color: #12166f;
              font-family: FedraSansStd-medium;
              font-weight: normal;
            }
          `}
        </style>
      </Box>
    </Box>
  );
};

export default IeoInPerson;
